import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Check,
  Info,
  Loader2,
  Paperclip,
  Plus,
  Send,
  ShieldCheck,
  TriangleAlert,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import ModalBase from "components/ModalBase";
import {
  abrirChamado,
  baixarAnexo,
  lerArquivo,
  listarChamados,
  resolverChamado,
  responderChamado,
  verChamado,
  type Chamado,
  type MensagemChamado,
  type StatusChamado,
} from "services/chamados";
import { getResumoCotacoes } from "services/cotacoes/cotacoes.service";
import { corTransportadora } from "utils/rastreioTransportadora";
import { tempoRelativo } from "utils/ultimoEvento";
import { downloadArquivo } from "./ModalCotacao";

type Props = {
  show: boolean;
  title: string;
  setShowNumber: (value: React.SetStateAction<number>) => void;
  logo?: ReactNode;
};

const STATUS: Record<StatusChamado, { label: string; chip: string; ponto: string }> = {
  ACAO_CLIENTE: {
    label: "Ação necessária",
    chip: "bg-amber-50 text-amber-800",
    ponto: "bg-amber-500",
  },
  EM_ANALISE: {
    label: "Em análise pela Intrex",
    chip: "bg-blue-50 text-blue-700",
    ponto: "bg-brand-primary",
  },
  RESOLVIDO: {
    label: "Resolvida",
    chip: "bg-emerald-50 text-emerald-700",
    ponto: "bg-emerald-500",
  },
};

const AUTOR: Record<string, { nome: string; sigla: string; classe: string }> = {
  CLIENTE: { nome: "Você", sigla: "EU", classe: "bg-slate-200 text-slate-600" },
  INTREX: { nome: "Intrex", sigla: "IX", classe: "bg-brand-sidebar text-white" },
  TRANSPORTADORA: { nome: "Transportadora", sigla: "TR", classe: "bg-slate-100 text-slate-500" },
};

const dataHora = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const dataCurta = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y) return iso;
  const data = new Date(y, m - 1, d);
  return data.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
};

const ModalOcorrencias = ({ show, title, setShowNumber, logo }: Props) => {
  const [itens, setItens] = useState<Chamado[] | null>(null);
  const [erro, setErro] = useState(false);
  const [aba, setAba] = useState<"abertas" | "resolvidas">("abertas");
  const [selecionado, setSelecionado] = useState<number | null>(null);
  const [relatando, setRelatando] = useState(false);
  const [semProblemaPct, setSemProblemaPct] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    try {
      const r = await listarChamados();
      setItens(r.itens);
      setErro(false);
    } catch {
      setErro(true);
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    setItens(null);
    setErro(false);
    carregar();

    // Percentual de envios sem problema, do resumo de cotações. Falha sozinho:
    // é número de apoio do estado vazio, não pode derrubar a tela.
    getResumoCotacoes()
      .then((r) => {
        const total = r.total || 0;
        if (!total) return setSemProblemaPct(null);
        const comProblema =
          (r.por_status.EXCECAO ?? 0) + (r.por_status.RETORNADO ?? 0);
        setSemProblemaPct(((total - comProblema) / total) * 100);
      })
      .catch(() => setSemProblemaPct(null));
  }, [show, carregar]);

  useEffect(() => {
    if (show) return;
    setAba("abertas");
    setSelecionado(null);
    setRelatando(false);
  }, [show]);

  const abertas = useMemo(
    () => (itens ?? []).filter((c) => c.status !== "RESOLVIDO"),
    [itens],
  );
  const resolvidas = useMemo(
    () => (itens ?? []).filter((c) => c.status === "RESOLVIDO"),
    [itens],
  );
  const lista = aba === "abertas" ? abertas : resolvidas;

  // Mantém a seleção dentro da aba aberta.
  useEffect(() => {
    if (!lista.length) {
      setSelecionado(null);
      return;
    }
    setSelecionado((atual) =>
      atual && lista.some((c) => c.id === atual) ? atual : lista[0].id,
    );
  }, [lista]);

  const precisamDeVoce = abertas.filter((c) => c.status === "ACAO_CLIENTE").length;
  const emAnalise = abertas.filter((c) => c.status === "EM_ANALISE").length;

  /** Encerradas neste ano e quanto tempo levaram, do próprio histórico. */
  const estatisticas = useMemo(() => {
    const ano = new Date().getFullYear();
    const doAno = resolvidas.filter(
      (c) => c.resolvido_em && new Date(c.resolvido_em).getFullYear() === ano,
    );
    if (!doAno.length) return { noAno: 0, tempoMedioDias: null as number | null };

    const soma = doAno.reduce(
      (acc, c) =>
        acc +
        (new Date(c.resolvido_em as string).getTime() - new Date(c.criado_em).getTime()),
      0,
    );
    return { noAno: doAno.length, tempoMedioDias: soma / doAno.length / 86400000 };
  }, [resolvidas]);

  // Sem nenhuma ocorrência na vida: tela única, sem abas para onde ir.
  const vazio = itens !== null && itens.length === 0;
  // Sem abertas, mas com histórico: a aba "Abertas" mostra o estado bom e a
  // de resolvidas continua acessível.
  const semAbertas = aba === "abertas" && !abertas.length;

  return (
    <ModalBase
      show={show}
      onClose={() => setShowNumber(-1)}
      titulo={title}
      icone={logo ?? <TriangleAlert />}
      largura={vazio ? "660px" : "1040px"}
      corpoSemPadding
      subtitulo={
        itens === null
          ? "Carregando…"
          : vazio
            ? "Envios com problema aparecem aqui"
            : `${precisamDeVoce} ${precisamDeVoce === 1 ? "precisa" : "precisam"} de você · ${emAnalise} em análise pela Intrex`
      }
      acoes={
        vazio || relatando ? undefined : (
          <button
            onClick={() => setRelatando(true)}
            className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            <Plus className="h-4 w-4" />
            Relatar problema
          </button>
        )
      }
    >
      {erro ? (
        <p className="px-6 py-12 text-center text-sm text-slate-500">
          Não foi possível carregar as ocorrências agora.
        </p>
      ) : itens === null ? (
        <div className="flex items-center justify-center gap-2 px-6 py-14 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Carregando…
        </div>
      ) : relatando ? (
        <FormRelato
          onCancelar={() => setRelatando(false)}
          onCriado={async () => {
            setRelatando(false);
            await carregar();
          }}
        />
      ) : vazio ? (
        <TudoCerto
          resolvidasNoAno={estatisticas.noAno}
          tempoMedioDias={estatisticas.tempoMedioDias}
          semProblemaPct={semProblemaPct}
          onRelatar={() => setRelatando(true)}
          onVerEnvios={() => setShowNumber(1)}
        />
      ) : (
        <>
          <div className="flex items-center gap-1 border-b border-slate-100 px-6">
            <Aba
              label="Abertas"
              n={abertas.length}
              ativa={aba === "abertas"}
              onClick={() => setAba("abertas")}
            />
            <Aba
              label="Resolvidas"
              n={resolvidas.length}
              ativa={aba === "resolvidas"}
              onClick={() => setAba("resolvidas")}
            />
          </div>

          {semAbertas ? (
            <TudoCerto
              resolvidasNoAno={estatisticas.noAno}
              tempoMedioDias={estatisticas.tempoMedioDias}
              semProblemaPct={semProblemaPct}
              onRelatar={() => setRelatando(true)}
              onVerEnvios={() => setShowNumber(1)}
              onVerHistorico={resolvidas.length ? () => setAba("resolvidas") : undefined}
            />
          ) : (
          <div className="grid h-[480px] grid-cols-1 md:grid-cols-[290px_1fr]">
            <ul className="min-h-0 space-y-2 overflow-y-auto border-slate-100 px-4 py-4 md:border-r">
              {!lista.length ? (
                <p className="px-2 py-8 text-center text-xs text-slate-400">
                  Nenhuma ocorrência resolvida ainda.
                </p>
              ) : (
                lista.map((c) => (
                  <ItemLista
                    key={c.id}
                    chamado={c}
                    ativo={c.id === selecionado}
                    onClick={() => setSelecionado(c.id)}
                  />
                ))
              )}
            </ul>

            <div className="min-h-0 overflow-hidden">
              {selecionado ? (
                <Detalhe id={selecionado} onMudou={carregar} />
              ) : (
                <p className="px-6 pt-16 text-center text-xs text-slate-400">
                  Selecione uma ocorrência.
                </p>
              )}
            </div>
          </div>
          )}
        </>
      )}
    </ModalBase>
  );
};

export default ModalOcorrencias;

/* ------------------------------------------------------------------ */

const Aba = ({
  label,
  n,
  ativa,
  onClick,
}: {
  label: string;
  n: number;
  ativa: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm transition-colors ${
      ativa
        ? "border-brand-sidebar font-semibold text-slate-900"
        : "border-transparent text-slate-500 hover:text-slate-700"
    }`}
  >
    {label}
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
        ativa ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"
      }`}
    >
      {n}
    </span>
  </button>
);

const ItemLista = ({
  chamado,
  ativo,
  onClick,
}: {
  chamado: Chamado;
  ativo: boolean;
  onClick: () => void;
}) => {
  const cfg = STATUS[chamado.status];
  return (
    <li>
      <button
        onClick={onClick}
        className={`w-full rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${
          ativo ? "border-brand-sidebar bg-slate-50" : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cfg.chip}`}>
            <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${cfg.ponto}`} />
            {cfg.label}
          </span>
          <span className="shrink-0 text-[10px] text-slate-400">
            {tempoRelativo(new Date(chamado.criado_em))}
          </span>
        </div>

        <p className="mt-1.5 truncate text-sm font-semibold text-slate-900">
          {chamado.titulo}
        </p>
        {chamado.envio && (
          <p className="truncate text-[11px] text-slate-500">
            {chamado.envio.pedido_ref}
            {chamado.envio.destino ? ` · ${chamado.envio.destino}` : ""}
          </p>
        )}

        {chamado.prazo && chamado.status === "ACAO_CLIENTE" && (
          <p className="mt-1.5 rounded-md bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-800">
            Responda até {dataCurta(chamado.prazo)}
          </p>
        )}
      </button>
    </li>
  );
};

const Detalhe = ({ id, onMudou }: { id: number; onMudou: () => Promise<void> }) => {
  const [chamado, setChamado] = useState<Chamado | null>(null);
  const [mensagens, setMensagens] = useState<MensagemChamado[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [texto, setTexto] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const inputArquivo = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await verChamado(id);
      setChamado(r.chamado);
      setMensagens(r.mensagens);
    } finally {
      setCarregando(false);
    }
  }, [id]);

  useEffect(() => {
    setTexto("");
    setArquivo(null);
    carregar();
  }, [carregar]);

  const enviar = async () => {
    if (!texto.trim() && !arquivo) return;
    setEnviando(true);
    try {
      const anexo = arquivo ? await lerArquivo(arquivo) : undefined;
      await responderChamado(id, { texto: texto.trim() || undefined, anexo });
      setTexto("");
      setArquivo(null);
      await carregar();
      await onMudou();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Não foi possível enviar.");
    } finally {
      setEnviando(false);
    }
  };

  const encerrar = async () => {
    try {
      await resolverChamado(id);
      toast.success("Ocorrência encerrada");
      await carregar();
      await onMudou();
    } catch {
      toast.error("Não foi possível encerrar.");
    }
  };

  const baixar = async (mensagem: MensagemChamado) => {
    try {
      const blob = await baixarAnexo(id, mensagem.id);
      downloadArquivo(blob, mensagem.anexo_nome ?? "anexo");
    } catch {
      toast.error("Não foi possível baixar o anexo.");
    }
  };

  if (carregando && !chamado) {
    return (
      <div className="flex items-center justify-center gap-2 pt-16 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Carregando…
      </div>
    );
  }
  if (!chamado) return null;

  const cfg = STATUS[chamado.status];
  const cor = corTransportadora(chamado.envio?.carrier);

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
        {/* Cabeçalho */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cfg.chip}`}>
                <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${cfg.ponto}`} />
                {cfg.label}
              </span>
              <span className="text-[11px] text-slate-400">
                Aberta em {dataHora(chamado.criado_em)}
              </span>
            </p>
            <h3 className="mt-1 text-lg font-semibold leading-tight text-slate-900">
              {chamado.titulo}
            </h3>
            {chamado.envio && (
              <p className="mt-0.5 text-xs text-slate-500">
                Envio <strong className="font-medium">{chamado.envio.pedido_ref}</strong>
                {chamado.envio.destino ? ` · ${chamado.envio.destino}` : ""}
                {chamado.envio.comprador ? ` · ${chamado.envio.comprador}` : ""}
              </p>
            )}
          </div>

          {chamado.envio?.carrier && (
            <span
              className="shrink-0 rounded-lg px-2 py-1 text-[10px] font-bold text-white"
              style={{ backgroundColor: cor ?? "#64748b" }}
            >
              {chamado.envio.carrier}
            </span>
          )}
        </div>

        {/* O que aconteceu */}
        {chamado.descricao && (
          <div className="flex gap-2.5 rounded-xl bg-slate-50 px-3 py-2.5">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <div>
              <p className="text-xs font-semibold text-slate-700">O que aconteceu</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-slate-600">
                {chamado.descricao}
              </p>
            </div>
          </div>
        )}

        {/* O que fazer */}
        {chamado.acao && chamado.status !== "RESOLVIDO" && (
          <div className="rounded-xl border-2 border-brand-sidebar px-3 py-3">
            <div className="flex items-start justify-between gap-3">
              <p className="flex items-center gap-2 text-xs font-semibold text-slate-800">
                <span className="grid h-4 w-4 place-items-center rounded-full bg-brand-sidebar text-[9px] text-white">
                  !
                </span>
                O que você precisa fazer
              </p>
              {chamado.prazo && (
                <span className="shrink-0 text-[10px] font-medium text-amber-700">
                  Responda até {dataCurta(chamado.prazo)}
                </span>
              )}
            </div>
            <p className="mt-1.5 text-[11px] leading-relaxed text-slate-600">{chamado.acao}</p>

            <button
              type="button"
              onClick={() => inputArquivo.current?.click()}
              className="mt-2.5 flex w-full items-center justify-between gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-3 py-2.5 text-left transition-colors hover:border-brand-primary hover:bg-blue-50/40"
            >
              <span className="flex items-center gap-2">
                <Upload className="h-4 w-4 text-slate-400" />
                <span>
                  <span className="block text-xs font-medium text-slate-700">
                    {arquivo ? arquivo.name : "Escolher arquivo"}
                  </span>
                  <span className="block text-[10px] text-slate-400">
                    PDF, JPG ou PNG até 10 MB
                  </span>
                </span>
              </span>
              <span className="shrink-0 rounded-lg bg-brand-sidebar px-3 py-1.5 text-[11px] font-semibold text-white">
                Procurar
              </span>
            </button>
          </div>
        )}

        {/* Acompanhamento */}
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Acompanhamento
          </p>
          <ul className="space-y-2">
            {mensagens.map((m) => {
              const autor = AUTOR[m.autor] ?? AUTOR.INTREX;
              return (
                <li key={m.id} className="flex gap-2.5">
                  <span
                    className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-[9px] font-bold ${autor.classe}`}
                  >
                    {autor.sigla}
                  </span>
                  <div className="min-w-0 flex-1 rounded-lg bg-slate-50 px-3 py-2">
                    <p className="text-[11px]">
                      <span className="font-semibold text-slate-700">{autor.nome}</span>{" "}
                      <span className="text-slate-400">{dataHora(m.criado_em)}</span>
                    </p>
                    {m.texto && (
                      <p className="mt-0.5 whitespace-pre-wrap text-[11px] leading-relaxed text-slate-600">
                        {m.texto}
                      </p>
                    )}
                    {m.tem_anexo && (
                      <button
                        onClick={() => baixar(m)}
                        className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-brand-primary hover:underline"
                      >
                        <Paperclip className="h-3 w-3" />
                        {m.anexo_nome ?? "anexo"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
            {!mensagens.length && (
              <li className="text-[11px] text-slate-400">Nenhuma mensagem ainda.</li>
            )}
          </ul>
        </div>

        {chamado.status !== "RESOLVIDO" && (
          <button
            onClick={encerrar}
            className="text-[11px] font-medium text-slate-500 transition-colors hover:text-slate-700"
          >
            Marcar como resolvida
          </button>
        )}
      </div>

      {/* Composer */}
      {chamado.status !== "RESOLVIDO" && (
        <div className="shrink-0 border-t border-slate-100 px-6 py-3">
          {arquivo && (
            <p className="mb-2 flex items-center gap-2 text-[11px] text-slate-500">
              <Paperclip className="h-3 w-3" />
              {arquivo.name}
              <button
                onClick={() => setArquivo(null)}
                className="text-slate-400 hover:text-red-500"
              >
                remover
              </button>
            </p>
          )}
          <div className="flex items-center gap-2">
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  enviar();
                }
              }}
              placeholder="Escreva para o suporte da Intrex…"
              className="h-10 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-400 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
            />
            <button
              onClick={() => inputArquivo.current?.click()}
              aria-label="Anexar arquivo"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50"
            >
              <Paperclip className="h-4 w-4" />
            </button>
            <button
              onClick={enviar}
              disabled={enviando || (!texto.trim() && !arquivo)}
              className="flex h-10 shrink-0 items-center gap-2 rounded-lg bg-brand-sidebar px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
            >
              {enviando ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Enviar
            </button>
          </div>
        </div>
      )}

      <input
        ref={inputArquivo}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) setArquivo(f);
          e.target.value = "";
        }}
      />
    </div>
  );
};

const FormRelato = ({
  onCancelar,
  onCriado,
}: {
  onCancelar: () => void;
  onCriado: () => Promise<void>;
}) => {
  const [titulo, setTitulo] = useState("");
  const [texto, setTexto] = useState("");
  const [salvando, setSalvando] = useState(false);

  const enviar = async () => {
    if (!titulo.trim()) return;
    setSalvando(true);
    try {
      await abrirChamado({ titulo: titulo.trim(), texto: texto.trim() || undefined });
      toast.success("Problema relatado. A Intrex responde por aqui.");
      await onCriado();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Não foi possível abrir o chamado.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-4 px-6 py-5">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Relatar um problema</h3>
        <p className="mt-0.5 text-xs text-slate-400">
          Abre uma ocorrência para o suporte da Intrex. Você acompanha a resposta por aqui.
        </p>
      </div>

      <label className="block">
        <span className="mb-1.5 block text-[11px] font-medium text-slate-500">
          O que aconteceu
        </span>
        <input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ex.: pacote entregue no endereço errado"
          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-300 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
        />
      </label>

      <label className="block">
        <span className="mb-1.5 block text-[11px] font-medium text-slate-500">
          Detalhes (opcional)
        </span>
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          rows={4}
          placeholder="Número do envio, o que o destinatário relatou, o que já foi tentado…"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-300 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
        />
      </label>

      <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-4">
        <button
          onClick={onCancelar}
          className="text-sm text-slate-500 transition-colors hover:text-slate-700"
        >
          Cancelar
        </button>
        <button
          onClick={enviar}
          disabled={!titulo.trim() || salvando}
          className="flex h-10 items-center gap-2 rounded-lg bg-brand-sidebar px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Abrir ocorrência
        </button>
      </div>
    </div>
  );
};

const TudoCerto = ({
  resolvidasNoAno,
  tempoMedioDias,
  semProblemaPct,
  onRelatar,
  onVerEnvios,
  onVerHistorico,
}: {
  resolvidasNoAno: number;
  tempoMedioDias: number | null;
  semProblemaPct: number | null;
  onRelatar: () => void;
  onVerEnvios: () => void;
  onVerHistorico?: () => void;
}) => (
  <div className="flex flex-col items-center px-6 py-10 text-center">
    <div className="mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald-50 text-emerald-600">
      <ShieldCheck className="h-7 w-7" />
    </div>
    <h3 className="text-lg font-semibold text-slate-900">Tudo certo com seus envios</h3>
    <p className="mt-1 max-w-[46ch] text-xs leading-relaxed text-slate-400">
      Nenhum envio com problema agora. Acompanhamos o rastreio o tempo todo e avisamos aqui
      se algo acontecer.
    </p>

    <div className="mt-5 grid w-full max-w-md grid-cols-3 gap-3">
      <Estatistica rotulo="Resolvidas" valor={String(resolvidasNoAno)} />
      <Estatistica
        rotulo="Tempo médio"
        valor={tempoMedioDias === null ? "—" : `${tempoMedioDias.toFixed(1)} dia`}
      />
      <Estatistica
        rotulo="Envios sem problema"
        valor={semProblemaPct === null ? "—" : `${semProblemaPct.toFixed(1)}%`}
      />
    </div>

    <div className="mt-5 flex items-center gap-2">
      {onVerHistorico && (
        <button
          onClick={onVerHistorico}
          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          Ver histórico
        </button>
      )}
      <button
        onClick={onRelatar}
        className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
      >
        Relatar problema
      </button>
      <button
        onClick={onVerEnvios}
        className="rounded-lg bg-brand-sidebar px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
      >
        Ver envios
      </button>
    </div>
  </div>
);

const Estatistica = ({ rotulo, valor }: { rotulo: string; valor: string }) => (
  <div className="rounded-xl bg-slate-50 px-3 py-2.5">
    <p className="text-[10px] leading-tight text-slate-400">{rotulo}</p>
    <p className="mt-0.5 text-base font-bold text-slate-900">{valor}</p>
  </div>
);
